import { resourceIdFilter } from "../resourceIdFilter";
import { tagFilter } from "../tagFilter";

import type { StaticFilterField } from "./StaticFilterField";

/**
 * The `dataset.dataset_id` row of the Sessions filter picker: narrows to
 * sessions holding at least one file from a given dataset. Appended by every
 * Sessions mount whose scope leaves the dataset axis free (the top-level
 * `/sessions` page, the device-scoped tab, and a collection's Sessions tab);
 * the dataset-scoped tab omits it because that axis is already pinned
 * server-side.
 */
export const sessionDatasetIdFilter = {
  type: "string",
  field: "dataset.dataset_id",
  name: "Dataset ID",
  ...resourceIdFilter,
} as const satisfies StaticFilterField;

/**
 * The `device.device_id` row of the Sessions filter picker: narrows to
 * sessions attached to a given device. Appended by every Sessions mount whose
 * scope leaves the device axis free; the device-scoped tab omits it because
 * that axis is already pinned server-side.
 */
export const sessionDeviceIdFilter = {
  type: "string",
  field: "device.device_id",
  name: "Device ID",
  ...resourceIdFilter,
} as const satisfies StaticFilterField;

/**
 * The rows every Sessions mount shows, in picker order. Each `field` is the
 * RoboQL token (alias or canonical label) the backend route forwards to
 * `SessionPredicateGenerator`, which is what `catalog.test.ts` pins against
 * the generated catalog.
 *
 * `tags` is a free-text string row rather than a `set` one because the
 * backend exposes no Sessions tag autocomplete endpoint; until it does, users
 * type tag values directly.
 */
export const SESSION_CORE_FILTER_FIELDS = [
  { type: "string", field: "id", name: "Session ID", ...resourceIdFilter },
  { type: "string", field: "name", name: "Name" },
  { type: "date", field: "start_time", name: "Start time" },
  { type: "date", field: "end_time", name: "End time" },
  { type: "numeric", field: "duration", name: "Duration (ns)" },
  { type: "identity", field: "created_by", name: "Created By" },
  { type: "identity", field: "modified_by", name: "Modified By" },
  { type: "string", field: "tags", name: "Tags", ...tagFilter },
] as const satisfies readonly StaticFilterField[];

/**
 * Every row a Sessions View can filter on. A mount picks the scope rows its
 * own scope leaves free; a View saved from any of them may carry either.
 */
export const SESSION_FILTER_FIELDS = [
  ...SESSION_CORE_FILTER_FIELDS,
  sessionDatasetIdFilter,
  sessionDeviceIdFilter,
] as const satisfies readonly StaticFilterField[];
